import React, { useState, useCallback } from 'react';
import { Box, Container } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, PageTitleBar } from '@/components';
import FilterModuleRangeInterval from '@/components/filters/FilterModuleRangeInterval';
import { notificationService } from '@core/notifications/NotificationService';
import { getMenuData } from '@core/data/menuData';
import WcnApexchart from '@/components/charts/WcnApexchart';
import AppCard from '@/components/appcard/AppCard';
import { zoneTrackerService } from '@/services/zoneTrackerService';
import { FCSecurePage } from '@/components/shared/navigation/securePage.ts';

interface FilterArgs {
    deviceId: string;
    moduleId: string;
    start: string;
    end: string;
    interval: number;
}

const ZoneTrackerEntries: FCSecurePage = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [series, setSeries] = useState<any[]>([]);

    const loadData = useCallback(
        async (args: FilterArgs) => {
            setLoading(true);
            setSeries([]);

            try {
                const data = await zoneTrackerService.loadEntries(
                    args.deviceId,
                    args.moduleId,
                    args.start,
                    args.end,
                    args.interval
                );

                const newSeries = [
                    {
                        name: t('entries'),
                        data: data.map((element) => ({
                            x: element.intervalStart,
                            y: element.entries
                        }))
                    }
                ];

                setSeries(newSeries);
            } catch (error) {
                console.error('Failed to load zone tracker entries:', error);
                notificationService.error(t('errorLoadingData'));
            } finally {
                setLoading(false);
            }
        },
        [t]
    );

    return (
        <Box sx={{ width: '100%', minHeight: '100%', bgcolor: 'background.default' }}>
            <PageTitleBar
                menus={getMenuData()}
                currentRoute={{
                    path: '/zonetracker/entries',
                    meta: {
                        breadcrumb: {
                            breadcrumbInactive: 'Statistics / Zone Tracker',
                            breadcrumbActive: 'Entries'
                        }
                    }
                }}
                t={t}
                titleSlot={`${t('zoneTracker')} ${t('entries')}`}
            />
            <Container maxWidth={false} sx={{ py: 3 }}>
                <FilterModuleRangeInterval
                    onLoadData={loadData}
                    forType="zoneTracker"
                    intervals={[30, 60]}
                    interval={60}
                />

                {loading ? (
                    <LoadingSpinner showBackground={false} />
                ) : (
                    <AppCard
                        heading={t('entries')}
                        icon="border_all"
                        iconClass=""
                        colClasses="flex col-12"
                        customClasses="mb-0"
                        fullBlock={false}
                        footer={false}
                    >
                        <WcnApexchart
                            type="bar"
                            chartId="zoneTrackerEntries"
                            series={series}
                            height={550}
                            xAxisType="datetime"
                            xAxisShowTime={true}
                        />
                    </AppCard>
                )}
            </Container>
        </Box>
    );
};

ZoneTrackerEntries.displayName = 'ZoneTrackerEntries';

export default ZoneTrackerEntries;
